import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { usePageActions } from '@hooks/usePageActions';
import { editPathway, fetchPathways } from '@pathways/redux/pathways/actions';
import { fetchIndexEvents } from '@pathways/redux/indexEvents/actions';
import { fetchRules } from '@pathways/redux/rules/actions';
import { selectPathwayEditing, selectPathwayWithRules } from '@pathways/redux/pathways/reducers';
import { selectIndexEvents } from '@pathways/redux/indexEvents/reducers';
import { extractSlugFromTypeSlug } from '@pathways/redux/indexEvents/utils';
import { matchType } from '../../../propTypes';
import PathwayWizard from './PathwayWizard';
import './style.less';

function EditPathway({ match }) {
  const [initialData, setInitialData] = useState({
    details: {},
    stages: {},
    stagedRules: {},
    indexEvents: {},
    indexEventRules: {
      indexEvents: [],
    },
    careTeamRoles: [],
    careTeamPermissions: {},
    callToAction: [],
  });
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const editing = useSelector(selectPathwayEditing);
  const [loadingPathway, existingPathway] = useSelector(state =>
    selectPathwayWithRules(state, match.params.id),
  );
  const [loadingIndexEvents, indexEventsList] = useSelector(selectIndexEvents);

  const loading = loadingPathway || loadingIndexEvents;

  const { setTitle } = usePageActions({
    title: '',
  });

  const onCompleted = useCallback(
    formData => {
      const roles =
        formData.careTeamPermissions?.careTeamPermissions?.map(careTeamPermission => ({
          name: careTeamPermission.name,
          description: careTeamPermission.description,
          permissions: careTeamPermission.permissions.map(permission => ({
            id: permission.id ?? permission.rule.id,
            contentRef: permission.contentRef ?? permission.rule.whatDetail?.content,
            permissionType: permission.permissionType,
            type: permission.type,
          })),
        })) || [];

      const pathwayData = {
        ...formData.details,
        language: formData.details.language || 'en',
        ...formData.stagedRules,
        ...formData.indexEvents,
        ...formData.indexEventRules,
        roles,
        isActive: existingPathway.isActive,
        isDeleted: existingPathway.isDeleted,
      };

      dispatch(editPathway(match.params.id, pathwayData));
    },
    [dispatch, existingPathway, match],
  );

  useEffect(() => {
    dispatch(fetchPathways());
    dispatch(fetchIndexEvents());
    dispatch(fetchRules());
  }, [dispatch]);

  useEffect(() => {
    if (existingPathway) {
      const {
        name,
        description,
        metadata,
        indexEvents,
        isActive,
        isManagedPathway,
      } = existingPathway;

      const indexEventTypes = indexEvents.reduce((acc, { eventTypeSlug }) => {
        const event = indexEventsList.find(
          ({ slug }) => extractSlugFromTypeSlug(eventTypeSlug) === slug,
        );
        return event ? [...acc, event] : acc;
      }, []);

      // Build a map of all rules by id for roles reconstruction
      const allRulesMap = new Map();
      existingPathway.stages.forEach(stage => {
        (stage.rules || []).forEach(rule => {
          allRulesMap.set(rule.id, rule);
        });
      });
      indexEvents.forEach(ie => {
        (ie.rules || []).forEach(rule => {
          allRulesMap.set(rule.id, rule);
        });
      });

      const allCTAsMap = new Map();
      (existingPathway.clientConfiguration?.callToAction || []).forEach(cta => {
        allCTAsMap.set(cta.id, cta);
      });

      // Process careTeam data if it exists
      const careTeamData = existingPathway.roles || [];
      const careTeamForForm = careTeamData.map(role => ({
        name: role.name,
        description: role.description || '',
      }));

      // Process careTeamPermissions data if roles exist
      const careTeamPermissionsData = careTeamData.map(role => ({
        name: role.name,
        description: role.description || '',
        permissions: (role.permissions || []).map(permission => {
          if (permission.type === 'cta') {
            const cta = allCTAsMap.get(permission.id);
            return {
              callToAction: cta || {},
              permissionType: permission.permissionType,
              contentRef: permission.contentRef,
              id: permission.id,
              type: permission.type,
            };
          }
          const rule = allRulesMap.get(permission.id);
          return {
            rule: rule || {},
            permissionType: permission.permissionType,
            contentRef: permission.contentRef,
            id: permission.id,
            type: permission.type || 'rule',
          };
        }),
      }));

      setTitle(`${t('common:buttons.edit')} ${name}`);
      document.title = `${t('common:buttons.edit')} ${name}`;
      setInitialData({
        details: {
          name,
          description,
          isManagedPathway,
          language: metadata.language,
          isActive: isActive,
        },
        stages: {
          ...existingPathway.stages.reduce((prev, stage) => {
            if (stage.isAdhoc) return prev;
            const stageNumber = `0${stage.number}`.slice(-2);
            return {
              ...prev,
              [`stage-${stageNumber}--name`]: stage.name,
              [`stage-${stageNumber}--description`]: stage.description,
            };
          }, {}),
        },
        stagedRules: {
          stages: existingPathway.stages.map(stage => ({
            ...stage,
            stageKey: `stage-${`0${stage.number}`.slice(-2)}`,
          })),
        },
        indexEvents: { indexEventTypes },
        indexEventRules: {
          indexEvents: indexEvents.map(ie => ({
            ...indexEventsList.find(({ slug }) => ie.eventTypeSlug === slug),
            ...ie,
          })),
        },
        careTeamRoles: careTeamForForm,
        careTeamPermissions: {
          careTeamPermissions: careTeamPermissionsData,
        },
        callToAction: existingPathway.clientConfiguration?.callToAction || [],
      });
    }
  }, [loading, match.params.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PathwayWizard
      initialData={initialData}
      loading={loading}
      submitting={editing}
      onCompleted={onCompleted}
    />
  );
}

EditPathway.propTypes = {
  match: matchType.isRequired,
};

export default EditPathway;
